import { useEffect, useState } from 'react'
import { AlertTriangle, ArrowRight, Check, Loader2, Snowflake } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  useCreateTransfer,
  useReadyTransfer,
  useTransferCandidates,
  useTransferPreview,
  useTransferReasons,
  useUpdateTransferMove,
} from '@/hooks/useStorageTransfers'
import type {
  CandidateResult,
  StorageTransferDetail,
  StorageTransferDisposition,
} from '@/types/storage'
import { STANDARD_TRANSFER_REASONS } from '@/types/storage'
import { CompatibilityReport } from './CompatibilityReport'
import { MovePreviewTable } from './MovePreviewTable'
import { MovePopoverContainer } from './popoverContainer'
import {
  applyPreviewEdits,
  approvalBlockers,
  dispositionStatement,
  previewEditsPayload,
  summariseMoves,
  type PreviewEdit,
} from './movePreview'

/**
 * Plan a storage unit's replacement.
 *
 * Four steps, in the order the decisions actually depend on each other: pick
 * the machine, read what is wrong with the pairing, settle the individual
 * moves, then say why and approve.
 *
 * Nothing is written until step 4. Steps 1–3 run entirely off `preview`, which
 * persists nothing — a wizard that created a DRAFT on the first Next would
 * leave a trail of abandoned plans behind every person who changed their mind,
 * and each of those would hold the partial-unique index against the real one.
 */

type Step = 1 | 2 | 3 | 4

export function ReplaceUnitDialog({
  open,
  onClose,
  sourceRootId,
  sourceCode,
  onPlanned,
}: {
  open: boolean
  onClose: () => void
  sourceRootId: string
  sourceCode: string
  /** Called with the approved plan, so the caller can navigate to it. */
  onPlanned?: (transfer: StorageTransferDetail) => void
}) {
  const [step, setStep] = useState<Step>(1)
  const [targetRootId, setTargetRootId] = useState<string | null>(null)
  const [reasonCode, setReasonCode] = useState('')
  const [reasonNote, setReasonNote] = useState('')
  const [disposition, setDisposition] = useState<StorageTransferDisposition | null>(null)
  const [created, setCreated] = useState<StorageTransferDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Which row is mid-save, so only that row's buttons go quiet. */
  const [pendingOrdinal, setPendingOrdinal] = useState<number | null>(null)
  /**
   * Rows settled on the preview, before any draft exists (z8nrz7f2gh). Held
   * here, not in the table, so they survive Back / Next, and sent with the
   * create so the draft starts from them.
   */
  const [edits, setEdits] = useState<Map<number, PreviewEdit>>(() => new Map())
  // The dialog's own element, where the row popovers render (popoverContainer.ts).
  const [dialogEl, setDialogEl] = useState<HTMLDivElement | null>(null)

  const candidates = useTransferCandidates(open ? sourceRootId : null)
  const reasons = useTransferReasons()
  const preview = useTransferPreview(
    open && targetRootId ? { sourceRootId, targetRootId } : null,
  )
  const createTransfer = useCreateTransfer()
  const updateMove = useUpdateTransferMove(created?.id ?? '')
  const readyTransfer = useReadyTransfer()

  useEffect(() => {
    if (!open) return
    setStep(1)
    setTargetRootId(null)
    setReasonCode('')
    setReasonNote('')
    setDisposition(null)
    setCreated(null)
    setError(null)
    setEdits(new Map())
  }, [open])

  // Edits belong to one pairing: a different replacement is a different plan.
  useEffect(() => setEdits(new Map()), [targetRootId])

  const candidate =
    (candidates.data?.units ?? []).find((c) => c.nodeId === targetRootId) ?? null

  // Before the plan exists these are the previewed moves; afterwards they are
  // the persisted rows, so per-row edits are reflected without a re-preview.
  const moves = created?.moves ?? applyPreviewEdits(preview.data?.moves ?? [], edits)
  const summary = summariseMoves(moves)
  const blockers = approvalBlockers(moves)

  // The catalog, falling back to the mirrored constants so the dropdown is
  // never empty while the request is in flight.
  const reasonRows = reasons.data?.length
    ? reasons.data
    : STANDARD_TRANSFER_REASONS.map((r, i) => ({ ...r, id: `standard-${i}` }))
  const reasonRow = reasonRows.find((r) => r.code === reasonCode) ?? null
  const effectiveDisposition = disposition ?? reasonRow?.disposition ?? null
  const noteRequired = reasonRow !== null && !reasonRow.isSystem

  const canApprove =
    reasonCode !== '' &&
    effectiveDisposition !== null &&
    (!noteRequired || reasonNote.trim() !== '') &&
    blockers.length === 0

  const busy = createTransfer.isPending || readyTransfer.isPending || updateMove.isPending

  /** Create the DRAFT if it does not exist yet, so per-move edits can persist. */
  const ensureDraft = async (): Promise<StorageTransferDetail | null> => {
    if (created) return created
    if (!targetRootId || !reasonCode || !effectiveDisposition) return null
    try {
      const draft = await createTransfer.mutateAsync({
        sourceRootId,
        targetRootId,
        reasonCode,
        reasonNote: reasonNote.trim() || undefined,
        disposition: effectiveDisposition,
        moves: edits.size > 0 ? previewEditsPayload(preview.data?.moves ?? [], edits) : undefined,
      })
      setCreated(draft)
      return draft
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the plan')
      return null
    }
  }

  /**
   * Apply one per-row edit and fold the returned row back in.
   *
   * The response is merged rather than the whole plan refetched: the backend
   * recomputes `targetNodePath` and clears the skip on a retarget, so the row
   * it returns is the truth, and refetching a thousand-move plan per keystroke
   * is not.
   */
  const editMove = (
    plan: StorageTransferDetail,
    ordinal: number,
    payload: { targetNodeRef?: string; exclude?: boolean; skipReason?: string },
  ) => {
    const row = plan.moves.find((r) => r.ordinal === ordinal)
    if (!row) return
    setError(null)
    setPendingOrdinal(ordinal)
    updateMove.mutate(
      { moveId: row.id, ...payload },
      {
        onSuccess: (updated) =>
          setCreated({
            ...plan,
            moves: plan.moves.map((r) => (r.id === updated.id ? updated : r)),
          }),
        onError: (e) => setError(e.message),
        onSettled: () => setPendingOrdinal(null),
      },
    )
  }

  const approve = async () => {
    setError(null)
    const draft = await ensureDraft()
    if (!draft) return
    try {
      // `draft.id`, never `created?.id`: `ensureDraft` has just called
      // `setCreated`, and that state is not readable until the next render —
      // reading it here sent an empty id and 404'd on `//ready`.
      await readyTransfer.mutateAsync(draft.id)
      onPlanned?.(draft)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not approve the plan')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent ref={setDialogEl} className='flex max-h-[88vh] flex-col gap-0 p-0 sm:max-w-5xl'>
        <MovePopoverContainer.Provider value={dialogEl}>
        <DialogHeader className='border-b border-gray-100 px-6 pb-4 pt-5'>
          <DialogTitle className='flex items-center gap-2'>
            <Snowflake className='h-5 w-5 text-sky-600' />
            Replace {sourceCode}
          </DialogTitle>
          <DialogDescription>
            Plan where this unit's contents go. Nothing moves until a technician executes the
            plan — this only decides where.
          </DialogDescription>
        </DialogHeader>

        <StepBar step={step} />

        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
          {step === 1 && (
            <CandidateStep
              loading={candidates.isLoading}
              result={candidates.data ?? null}
              selected={targetRootId}
              onSelect={setTargetRootId}
              sourceCode={sourceCode}
            />
          )}

          {step === 2 && candidate && (
            preview.isLoading ? (
              <Loading label='Working out the mapping…' />
            ) : (
              <CompatibilityReport
                candidate={candidate}
                moves={moves}
                sourceCode={sourceCode}
              />
            )
          )}

          {step === 3 &&
            (preview.isLoading ? (
              <Loading label='Working out the mapping…' />
            ) : (
              <div className='space-y-3'>
                <p className='text-sm text-muted-foreground'>
                  {summary.total} occupant{summary.total === 1 ? '' : 's'} · {summary.planned}{' '}
                  planned
                  {summary.excluded > 0 && ` · ${summary.excluded} excluded`}
                  {blockers.length > 0 && ` · ${blockers.reduce((n, b) => n + b.count, 0)} to settle`}
                </p>
                <MovePreviewTable
                  moves={moves}
                  actions={
                    created
                      ? {
                          busyOrdinal: pendingOrdinal,
                          targetUnitId: created.targetRootId,
                          onRetarget: (m, ref) =>
                            editMove(created, m.ordinal, { targetNodeRef: ref }),
                          onExclude: (m, reason) =>
                            editMove(created, m.ordinal, { exclude: true, skipReason: reason }),
                        }
                      : {
                          // z8nrz7f2gh: settle rows here, before any draft —
                          // nothing is stored until the plan is saved.
                          targetUnitId: targetRootId ?? undefined,
                          onRetarget: (m, ref, path) =>
                            setEdits((prev) =>
                              new Map(prev).set(m.ordinal, {
                                kind: 'target',
                                targetNodeRef: ref,
                                targetNodePath: path,
                              }),
                            ),
                          onExclude: (m, reason) =>
                            setEdits((prev) =>
                              new Map(prev).set(m.ordinal, { kind: 'exclude', skipReason: reason }),
                            ),
                          onRestore: (m) =>
                            setEdits((prev) => {
                              const next = new Map(prev)
                              next.delete(m.ordinal)
                              return next
                            }),
                        }
                  }
                />
                {!created && (
                  <p className='rounded-md border border-sky-200 bg-sky-50 p-2 text-[13px] text-sky-900'>
                    This is a preview — nothing is saved until you save a draft or approve. Choose
                    or change a slot, or exclude an item, on any row.
                  </p>
                )}
                {blockers.length > 0 && (
                  <div className='space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900'>
                    {blockers.map((b) => (
                      <p key={b.kind}>{b.message}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {step === 4 && (
            <div className='space-y-4'>
              <div className='space-y-1.5'>
                <Label>Why is this unit being emptied?</Label>
                <Select
                  value={reasonCode}
                  onValueChange={(v) => {
                    setReasonCode(v)
                    // Follow the catalog until the approver overrides it.
                    setDisposition(null)
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Choose a reason…' />
                  </SelectTrigger>
                  <SelectContent>
                    {reasonRows.map((r) => (
                      <SelectItem key={r.code} value={r.code}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {reasonRow && (
                <div className='space-y-1.5'>
                  <Label>What happens to {sourceCode}?</Label>
                  <Select
                    value={effectiveDisposition ?? ''}
                    onValueChange={(v) => setDisposition(v as StorageTransferDisposition)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='RETURN_TO_SERVICE'>It comes back</SelectItem>
                      <SelectItem value='DECOMMISSION'>It is retired</SelectItem>
                      <SelectItem value='REMAINS_IN_SERVICE'>It stays in service</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Stated in plain words, not as an enum name: this is the
                      one choice whose consequences the approver cannot undo. */}
                  {effectiveDisposition && (
                    <p
                      className={cn(
                        'rounded-md border p-2 text-[13px]',
                        effectiveDisposition === 'DECOMMISSION'
                          ? 'border-amber-300 bg-amber-50 text-amber-900'
                          : 'border-gray-200 bg-muted/40 text-muted-foreground',
                      )}
                    >
                      {dispositionStatement(effectiveDisposition, sourceCode)}
                    </p>
                  )}
                </div>
              )}

              <div className='space-y-1.5'>
                <Label>
                  Note {noteRequired ? '(required for a site-defined reason)' : '(optional)'}
                </Label>
                <Textarea
                  value={reasonNote}
                  onChange={(e) => setReasonNote(e.target.value)}
                  rows={3}
                  placeholder='Anything the technician or a reviewer needs to know.'
                />
              </div>

              {blockers.length > 0 && (
                <div className='space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900'>
                  <p className='flex items-center gap-1.5 font-medium'>
                    <AlertTriangle className='h-4 w-4' />
                    The plan cannot be approved yet
                  </p>
                  {blockers.map((b) => (
                    <p key={b.kind} className='text-[13px]'>
                      {b.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <p className='mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-[13px] text-red-800'>
              {error}
            </p>
          )}
        </div>

        <div className='flex items-center justify-between gap-2 border-t border-gray-100 px-6 py-3'>
          <Button
            variant='ghost'
            disabled={step === 1 || busy}
            onClick={() => setStep((s) => (s - 1) as Step)}
          >
            Back
          </Button>

          <div className='flex items-center gap-2'>
            <Button variant='outline' onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            {step < 4 ? (
              <Button
                disabled={
                  (step === 1 && !targetRootId) ||
                  // Every occupant needs a destination or an exclusion before
                  // the plan moves on; the blockers above name which.
                  (step === 3 && (preview.isLoading || blockers.length > 0)) ||
                  busy
                }
                title={step === 3 && blockers.length > 0 ? blockers[0].message : undefined}
                onClick={() => setStep((s) => (s + 1) as Step)}
              >
                Next
                <ArrowRight className='ml-1 h-4 w-4' />
              </Button>
            ) : (
              <>
                {!created && (
                  <Button
                    variant='outline'
                    disabled={!reasonCode || !effectiveDisposition || busy}
                    onClick={() => {
                      setError(null)
                      void ensureDraft().then((d) => d && setStep(3))
                    }}
                  >
                    Save draft
                  </Button>
                )}
                <Button disabled={!canApprove || busy} onClick={() => void approve()}>
                  {busy ? (
                    <Loader2 className='mr-1 h-4 w-4 animate-spin' />
                  ) : (
                    <Check className='mr-1 h-4 w-4' />
                  )}
                  Approve plan
                </Button>
              </>
            )}
          </div>
        </div>
        </MovePopoverContainer.Provider>
      </DialogContent>
    </Dialog>
  )
}

function Loading({ label }: { label: string }) {
  return (
    <p className='py-10 text-center text-sm text-muted-foreground'>
      <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
      {label}
    </p>
  )
}

const STEP_LABELS = ['Replacement', 'Compatibility', 'Moves', 'Approve'] as const

function StepBar({ step }: { step: Step }) {
  return (
    <ol className='flex items-center gap-1 border-b border-gray-100 px-6 py-2 text-[13px]'>
      {STEP_LABELS.map((label, i) => {
        const n = (i + 1) as Step
        return (
          <li key={label} className='flex items-center gap-1'>
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium',
                n === step
                  ? 'bg-primary text-primary-foreground'
                  : n < step
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              {n < step ? <Check className='h-3 w-3' /> : n}
            </span>
            <span className={cn(n === step ? 'font-medium' : 'text-muted-foreground')}>
              {label}
            </span>
            {i < STEP_LABELS.length - 1 && <span className='mx-1 text-gray-300'>›</span>}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Only units at a COMPATIBLE storage condition are listed — the server
 * withholds the rest. What it does not do is hide the fact: a withheld count
 * is spelled out, because somebody who knows FRIDGE-1 is empty will otherwise
 * go looking for a bug in the list.
 */
function CandidateStep({
  loading,
  result,
  selected,
  onSelect,
  sourceCode,
}: {
  loading: boolean
  result: CandidateResult | null
  selected: string | null
  onSelect: (id: string) => void
  sourceCode: string
}) {
  if (loading) return <Loading label='Finding replacements…' />

  const candidates = result?.units ?? []
  const withheld = result?.excludedForCondition ?? 0
  const requires = result?.requires

  const conditionNote = (
    <p className='text-[13px] text-muted-foreground'>
      {result?.conditionsChecked
        ? `Only units running at ${requires?.replace(/_/g, ' ').toLowerCase()} are shown — ${sourceCode}'s contents cannot be stored at another condition.`
        : `${sourceCode} declares no storage condition, so these are units with room. Nothing was checked against temperature.`}
      {withheld > 0 &&
        ` ${withheld} other unit${withheld === 1 ? '' : 's'} ${withheld === 1 ? 'has' : 'have'} room but ${withheld === 1 ? 'runs' : 'run'} at a different condition.`}
    </p>
  )

  if (candidates.length === 0) {
    return (
      <div className='space-y-2 py-8 text-sm'>
        <p className='font-medium'>
          {withheld > 0
            ? `No unit at a compatible condition can take ${sourceCode}'s contents.`
            : `No unit can take ${sourceCode}'s contents right now.`}
        </p>
        {/* Two different empty states. "Nothing is the right temperature" and
            "nothing is free" send somebody to different places. */}
        <p className='text-muted-foreground'>
          {withheld > 0
            ? `${withheld} unit${withheld === 1 ? '' : 's'} ${withheld === 1 ? 'has' : 'have'} room but ${withheld === 1 ? 'runs' : 'run'} at a different storage condition, and stock cannot be moved into one. A replacement has to match ${requires?.replace(/_/g, ' ').toLowerCase()}.`
            : 'A replacement has to be an operational storage unit that is not already the source or target of a live transfer. Free one up, or cancel a plan that is holding it.'}
        </p>
      </div>
    )
  }

  return (
    <ul className='space-y-2'>
      <li>{conditionNote}</li>
      {candidates.map((c) => {
        const chosen = c.nodeId === selected
        return (
          <li key={c.nodeId}>
            <button
              type='button'
              onClick={() => onSelect(c.nodeId)}
              className={cn(
                'flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                chosen ? 'border-primary bg-primary/5' : 'hover:border-primary/40 hover:bg-primary/5',
              )}
            >
              <Snowflake
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  chosen ? 'text-primary' : 'text-muted-foreground',
                )}
              />
              <div className='min-w-0 flex-1'>
                <p className='font-medium'>
                  {c.name ?? c.code}
                  <span className='ml-2 font-normal text-muted-foreground'>{c.code}</span>
                </p>
                <p className='text-[13px] text-muted-foreground'>
                  {c.remaining} free of {c.spaces}
                  {c.condition && ` · ${c.condition.replace(/_/g, ' ').toLowerCase()}`}
                </p>
              </div>
              <div className='shrink-0 space-y-0.5 text-right text-[12px]'>
                {/* Every unit here is condition-compatible by construction, so
                    the only thing left to flag is room. A shortfall is shown
                    and ranked down rather than withheld: a unit with too few
                    slots can still take most of a freezer, with the remainder
                    excluded by hand. */}
                {c.fits ? (
                  <p className='text-emerald-700'>Fits</p>
                ) : (
                  <p className='text-amber-700'>{c.shortfall} short</p>
                )}
              </div>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
